/**
 * Suppliers from Excel, read in the browser (the server never parses an uploaded file): the template's
 * columns and the names PRPO accepts for each (mirrors backend utils/supplier.util.ts SUPPLIER_COLUMNS),
 * the rows of a sheet, and the result file written after the creation.
 */
import type { RowStatus, SupplierInput } from '../types/suppliers';

export interface SupplierColumn { field: keyof SupplierInput; header: string; aliases: string[] }

export const SUPPLIER_IMPORT_COLUMNS: SupplierColumn[] = [
  { field: 'name', header: 'Supplier name', aliases: ['supplier name', 'name', 'vendor name', 'supplier', 'vendor', 'card name', 'cardname'] },
  { field: 'foreignName', header: 'Arabic name', aliases: ['arabic name', 'foreign name', 'name (arabic)', 'cardforeignname'] },
  { field: 'vat', header: 'VAT registration number', aliases: ['vat registration number', 'vat number', 'vat no', 'vat no.', 'vat', 'trn', 'tax number', 'tax id', 'federal tax id', 'federaltaxid'] },
  { field: 'crn', header: 'CRN', aliases: ['crn', 'cr number', 'cr no', 'cr no.', 'commercial registration', 'commercial registration number', 'company registration number'] },
  { field: 'group', header: 'Group', aliases: ['group', 'vendor group', 'supplier group', 'groupcode'] },
  { field: 'currency', header: 'Currency', aliases: ['currency', 'currency code'] },
  { field: 'paymentTerms', header: 'Payment terms', aliases: ['payment terms', 'terms', 'payment term'] },
  { field: 'country', header: 'Country', aliases: ['country', 'country code'] },
  { field: 'city', header: 'City', aliases: ['city', 'town'] },
  { field: 'district', header: 'District', aliases: ['district', 'block', 'area'] },
  { field: 'street', header: 'Street', aliases: ['street', 'address', 'street address'] },
  { field: 'buildingNo', header: 'Building no.', aliases: ['building no.', 'building no', 'building number', 'building'] },
  { field: 'postalCode', header: 'Postal code', aliases: ['postal code', 'zip code', 'zip', 'postcode', 'zipcode'] },
  { field: 'mobile', header: 'Mobile', aliases: ['mobile', 'mobile number', 'cellular', 'mobile no', 'mobile no.'] },
  { field: 'phone', header: 'Phone', aliases: ['phone', 'telephone', 'phone number', 'tel', 'tel.'] },
  { field: 'email', header: 'E-mail', aliases: ['e-mail', 'email', 'email address', 'e-mail address'] },
  { field: 'contactPerson', header: 'Contact person', aliases: ['contact person', 'contact', 'contact name'] },
];

const BY_ALIAS = new Map<string, keyof SupplierInput>();
for (const c of SUPPLIER_IMPORT_COLUMNS) for (const alias of c.aliases) BY_ALIAS.set(alias, c.field);

const normalizeHeader = (value: unknown): string => String(value ?? '').replace(/[\s*]+/g, ' ').trim().toLowerCase();

/** The supplier field a column name stands for (a known name, any case, a trailing * ignored), or null. */
export function supplierFieldForHeader(header: unknown): keyof SupplierInput | null {
  return BY_ALIAS.get(normalizeHeader(header)) ?? null;
}

/** A cell as text: numbers without exponent or decimals noise (VAT numbers are 15 digits), dates as YYYY-MM-DD. */
export function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '';
    if (!Number.isInteger(value)) return String(value);
    // Excel keeps 15 significant digits: a longer number typed in a number cell lost its last digits.
    // Shown as Excel holds it (1.2345678901234567E+19) so the server refuses it instead of storing wrong digits.
    return Math.abs(value) < 1e15 ? value.toFixed(0) : value.toExponential().toUpperCase();
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
  return String(value).replace(/\s+/g, ' ').trim();
}

export interface ParsedRow { rowNum: number; values: Partial<SupplierInput> }

export interface ParsedSheet {
  rows: ParsedRow[];
  /** Column names PRPO does not know (left out). */
  unknownHeaders: string[];
  /** No row of column names: the columns were read in the template's order. */
  positional: boolean;
}

const HEADER_SEARCH_ROWS = 30;

/** The column names a row holds (each field once, first column wins). */
function headerFields(cells: unknown[]): Array<keyof SupplierInput | null> {
  const used = new Set<keyof SupplierInput>();
  return (cells || []).map((c) => {
    const field = supplierFieldForHeader(c);
    if (!field || used.has(field)) return null;
    used.add(field);
    return field;
  });
}

/**
 * The suppliers of a sheet (rows of cells, as SheetJS gives them with header: 1, blank rows included).
 * The header is the row, among the first 30, with the most known column names (a title such as
 * "Supplier" above it holds one); rows above it are skipped, as are empty rows. A column name used twice
 * keeps its first column. `firstRow` is the Excel row of table[0] (the sheet's range may start lower),
 * so rowNum is the row number Excel shows.
 */
export function parseSupplierTable(table: unknown[][], firstRow = 1): ParsedSheet {
  let headerIndex = -1;
  let best = 0;
  for (let i = 0; i < Math.min(table.length, HEADER_SEARCH_ROWS); i++) {
    const known = headerFields(table[i] || []).filter((f) => f !== null).length;
    if (known > best) { best = known; headerIndex = i; }
  }
  const positional = headerIndex < 0;
  const fields: Array<keyof SupplierInput | null> = positional
    ? SUPPLIER_IMPORT_COLUMNS.map((c) => c.field)
    : headerFields(table[headerIndex] || []);
  const unknownHeaders = positional
    ? []
    : (table[headerIndex] || [])
      .map((c, i) => (fields[i] === null && cellText(c) ? cellText(c) : ''))
      .filter(Boolean);
  const rows: ParsedRow[] = [];
  const start = positional ? 0 : headerIndex + 1;
  for (let i = start; i < table.length; i++) {
    const cells = table[i] || [];
    const values: Partial<SupplierInput> = {};
    let filled = false;
    fields.forEach((field, col) => {
      if (!field) return;
      const text = cellText(cells[col]);
      if (text) { values[field] = text; filled = true; }
    });
    if (filled) rows.push({ rowNum: firstRow + i, values });
  }
  return { rows, unknownHeaders, positional };
}

export const ROW_STATUS: Record<RowStatus | 'Created' | 'Failed' | 'Creating', { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
  Ready: { label: 'Ready', color: 'success' },
  Warning: { label: 'Ready, with a warning', color: 'warning' },
  Error: { label: 'Error', color: 'error' },
  AlreadyInSap: { label: 'Already in SAP', color: 'error' },
  DuplicateInFile: { label: 'Duplicate in file', color: 'warning' },
  Creating: { label: 'Creating…', color: 'info' },
  Created: { label: 'Created', color: 'success' },
  Failed: { label: 'SAP refused', color: 'error' },
};

export interface ResultRow {
  rowNum: number;
  values: Partial<SupplierInput>;
  status: string;
  cardCode: string;
  detail: string;
}

/** The result file: the template's columns as uploaded, then Row, Status, SAP code and Detail. */
export function resultSheet(rows: ResultRow[]): unknown[][] {
  const header = ['Row', ...SUPPLIER_IMPORT_COLUMNS.map((c) => c.header), 'Status', 'SAP code', 'Detail'];
  return [
    header,
    ...rows.map((r) => [
      r.rowNum,
      ...SUPPLIER_IMPORT_COLUMNS.map((c) => r.values[c.field] ?? ''),
      r.status,
      r.cardCode,
      r.detail,
    ]),
  ];
}
